import { inject, type InjectionKey } from "vue";

export type StepHeadingLevel = "h1" | "h2";
export type CardHeadingLevel = "h2" | "h3";
export type SubCardHeadingLevel = "h3" | "h4";

/**
 * What heading element a step screen's own title renders as, decided by whoever is hosting the step.
 *
 * On a wizard route a step screen IS the page, so its title is the `h1` — nothing else there claims
 * one. The settings page hosts every step at once, which under that default puts five to seven `h1`
 * elements on a single page: a document with no outline, and a screen-reader user navigating by
 * heading getting seven "level one"s for what are plainly sections of one thing.
 *
 * Injected rather than passed as a prop because the host does not render `StepIntro` or the cards —
 * it renders the STEP, which renders both inside itself. Threading a prop down would mean touching
 * every step component to forward something none of them care about. Provided once at the top,
 * defaulted here, so every existing caller keeps exactly the markup it had.
 */
export const stepHeadingLevelKey: InjectionKey<StepHeadingLevel> = Symbol("stepHeadingLevel");

/** The step's own title. `h1` unless a host says otherwise. */
export const useStepHeadingLevel = (): StepHeadingLevel => inject(stepHeadingLevelKey, "h1");

/**
 * Whether this step is a SECTION of a longer page rather than the page itself.
 *
 * Derived from the heading level rather than provided separately, because it is the same fact: a
 * host asks for `h2` exactly when the step is one of several on a document, and that is exactly when
 * a step's title should behave like a section header — pin to the top of the scroll container while
 * its section is being read, so the reader is never in an unlabelled part of a seven-section page.
 * Two provides for one truth is how the two drift apart.
 *
 * If a host ever wants `h2` WITHOUT section chrome, that is the moment this earns its own injection
 * key. Until there is such a host, a named reader over the existing one says what the call site
 * means without inventing state to say it.
 */
export const useStepIsPageSection = (): boolean => useStepHeadingLevel() === "h2";

/**
 * The id of the section this step occupies on its host page, when the host is one page of sections.
 *
 * Only the settings page provides it, and it provides a DIFFERENT value per section — which is why it
 * cannot ride along on `stepHeadingLevelKey`, a single value for the whole page. `SettingsSection.vue`
 * provides it around each step, so `StepIntro` can offer a link to the section it is heading without
 * every step component learning that it has an id at all.
 *
 * Empty string when there is no such id: a wizard route has one step and no fragment to link at.
 */
export const stepSectionIdKey: InjectionKey<string> = Symbol("stepSectionId");

/** The fragment identifying this step's section on the host page, or "" on a wizard route. */
export const useStepSectionId = (): string => inject(stepSectionIdKey, "");

/**
 * The title of a card WITHIN a step — "Name", "Above ground", "Heat supply".
 *
 * Always exactly one level below the step's own title, which is the whole point: these cards belong
 * to the step above them, and a heading level is how that is said. Pinning them at a fixed `h2`
 * meant that on the settings page — where the step title had become an `h2` — a card sat as the step's
 * SIBLING rather than its child, so the outline claimed "Name" and "What is this project?" were peers.
 */
export const useCardHeadingLevel = (): CardHeadingLevel => (useStepHeadingLevel() === "h1" ? "h2" : "h3");

/**
 * A heading nested INSIDE a card — "Stream URL", "Selected models", "Suggested models".
 *
 * Only the 3D model step goes this deep; every other card in WizardFlow ends at its own title. It is
 * still worth deriving rather than hardcoding, because hardcoding is precisely what broke: these
 * three were fixed at `h3`, which was right while a card was always `h2`, and became wrong the
 * moment the settings page pushed cards down to `h3` and made them their own children's siblings.
 */
export const useSubCardHeadingLevel = (): SubCardHeadingLevel => (useCardHeadingLevel() === "h2" ? "h3" : "h4");
